import React, { useEffect, useState } from 'react';
import {
  BUILTIN_PROFILES,
  loadCustomProfiles,
  saveCustomProfile,
  deleteCustomProfile,
  updateCustomProfile,
  applyProfile,
} from './seasonalProfiles';
import ProfileEditor from './ProfileEditor';
import { useP } from '../../../i18n/useP';

// Row of preset buttons under the sparkline. Click → apply the profile to
// the current annual total. "+ Nouveau" saves the current 12-month shape
// as a reusable custom profile (browser-local).
export default function SeasonalityPicker({
  values,            // calendar-indexed — unused now but kept for API symmetry
  currentTotal,
  disabled = false,
  onApply,           // called with new calendar-indexed values
}) {
  const p = useP();
  const [customProfiles, setCustomProfiles] = useState([]);
  // null = closed, 'create' = new, profile object = editing that profile
  const [editorState, setEditorState] = useState(null);

  useEffect(() => {
    setCustomProfiles(loadCustomProfiles());
  }, []);

  const nothingToWork = currentTotal <= 0;

  function handleApply(profile) {
    const next = applyProfile(currentTotal, profile.factors);
    onApply(next);
  }

  function handleProfileSaved(profile) {
    const existing = customProfiles.find((c) => c.id === profile.id);
    if (existing) {
      setCustomProfiles(updateCustomProfile(profile.id, profile));
    } else {
      setCustomProfiles(saveCustomProfile(profile));
    }
    setEditorState(null);
  }

  function handleDelete(id, event) {
    event.stopPropagation();
    if (!window.confirm(p('Supprimer ce profil personnalisé ?'))) return;
    setCustomProfiles(deleteCustomProfile(id));
  }

  function handleEdit(profile, event) {
    event.stopPropagation();
    setEditorState(profile);
  }

  return (
    <div className="billing-profile">
      <span className="billing-profile__label">
        <span aria-hidden="true">📈</span> {p('Profil de facturation (saisonnalité)')}
      </span>
      <div className="billing-profile__buttons">
        {BUILTIN_PROFILES.map((profile) => (
          <button
            key={profile.id}
            type="button"
            className="billing-profile__btn"
            onClick={() => handleApply(profile)}
            disabled={disabled || nothingToWork}
            title={nothingToWork
              ? p("Renseigne d'abord un total annuel.")
              : p('Appliquer "{{label}}"').replace('{{label}}', profile.label)}
          >
            {profile.label}
          </button>
        ))}
        {customProfiles.map((profile) => (
          <span key={profile.id} className="billing-profile__custom-wrap">
            <button
              type="button"
              className="billing-profile__btn billing-profile__btn--custom"
              onClick={() => handleApply(profile)}
              disabled={disabled || nothingToWork}
              title={p('Appliquer "{{label}}"').replace('{{label}}', profile.label)}
            >
              {profile.label}
            </button>
            <button
              type="button"
              className="billing-profile__edit"
              onClick={(e) => handleEdit(profile, e)}
              disabled={disabled}
              title={p('Modifier "{{label}}"').replace('{{label}}', profile.label)}
              aria-label={p('Modifier {{label}}').replace('{{label}}', profile.label)}
            >
              ✎
            </button>
            <button
              type="button"
              className="billing-profile__del"
              onClick={(e) => handleDelete(profile.id, e)}
              disabled={disabled}
              title={p('Supprimer ce profil')}
              aria-label={p('Supprimer {{label}}').replace('{{label}}', profile.label)}
            >
              ×
            </button>
          </span>
        ))}
        <button
          type="button"
          className="billing-profile__btn billing-profile__btn--add"
          onClick={() => setEditorState('create')}
          disabled={disabled}
          title={p('Saisir un nouveau profil mois par mois')}
        >
          {p('+ Nouveau profil')}
        </button>
      </div>

      {editorState && (
        <ProfileEditor
          initialProfile={editorState === 'create' ? null : editorState}
          onSave={handleProfileSaved}
          onCancel={() => setEditorState(null)}
        />
      )}
    </div>
  );
}
